import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiError, api, till } from "@/lib/api";
import { dateTime, fmtSerial, money, toMinor } from "@/lib/format";
import { beep, useScanner } from "@/lib/scanner";
import type { Item, Move } from "@/lib/types";
import { CameraScanner } from "@/components/CameraScanner";
import { Badge, Button, Field, Input, Select, cx, useToast } from "@/components/ui";
import { Card, PageHead, td, th } from "@/components/Layout";
import { useShop } from "@/lib/till-context";

type Line = { itemId: string; qty: string; cost: string; serials: string[] };
const KIND_TONE: Record<string, "slate" | "brand" | "blue" | "amber" | "red" | "violet"> = { sale: "slate", return: "brand", receive: "blue", transfer_in: "amber", transfer_out: "amber", rma: "red", writeoff: "red", stocktake: "violet", adjust: "violet" };
const KIND_LABEL: Record<string, string> = { sale: "Sale", return: "Return", receive: "Received", transfer_in: "From website", transfer_out: "To website", rma: "To supplier", writeoff: "Write-off", stocktake: "Stocktake", adjust: "Adjust" };

export function StockMoves() {
  const [sp, setSp] = useSearchParams();
  const shop = useShop();
  const tab = sp.get("tab") === "transfer" && shop.website ? "transfer" : "receive";
  const items = useQuery({ queryKey: ["admin-items"], queryFn: () => api<{ items: Item[] }>(till("/admin/items")) });
  return (
    <>
      <PageHead title={shop.website ? "Receive & transfer" : "Receive & count stock"} sub={`${shop.serials ? "Scan deliveries in unit by unit" : "Count deliveries in"} · every change to shop stock is logged`}>
        <Button tone={tab === "receive" ? "dark" : "light"} onClick={() => setSp({})}>
          Receive a delivery
        </Button>
        {shop.website ? (
          <Button tone={tab === "transfer" ? "dark" : "light"} onClick={() => setSp({ tab: "transfer" })}>
            Transfer shop ↔ website
          </Button>
        ) : null}
      </PageHead>
      <div className="flex flex-col gap-4">
        {tab === "receive" ? <Receive items={items.data?.items ?? []} preselect={sp.get("item")} /> : <Transfer items={items.data?.items ?? []} preselect={sp.get("item")} />}
        {shop.serials ? <FindUnit /> : null}
        <Log />
      </div>
    </>
  );
}

function Receive({ items, preselect }: { items: Item[]; preselect: string | null }) {
  const toast = useToast();
  const qc = useQueryClient();
  const [supplier, setSupplier] = useState("");
  const [ref, setRef] = useState("");
  const [lines, setLines] = useState<Line[]>([]);
  const [active, setActive] = useState<number>(-1);
  const [scan, setScan] = useState("");
  const [camera, setCamera] = useState(false);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string>("");
  const byId = useMemo(() => new Map(items.map((i) => [i.id, i])), [items]);

  useEffect(() => {
    if (preselect && byId.get(preselect) && !lines.length) {
      setLines([{ itemId: preselect, qty: "", cost: "", serials: [] }]);
      setActive(0);
    }
  }, [preselect, byId]); // eslint-disable-line react-hooks/exhaustive-deps

  /** A scan is either a product barcode (start/select that line) or a unit's IMEI for the selected device line. */
  const onScan = (raw: string) => {
    const code = raw.trim();
    setScan("");
    const item = items.find((i) => i.barcode === code || (i.sku && i.sku === code));
    if (item) {
      const idx = lines.findIndex((l) => l.itemId === item.id);
      if (idx >= 0) {
        setActive(idx);
        if (item.tracking === "qty") setLines(lines.map((l, i) => (i === idx ? { ...l, qty: String((Number(l.qty) || 0) + 1) } : l)));
      } else {
        setLines([...lines, { itemId: item.id, qty: item.tracking === "qty" ? "1" : "", cost: "", serials: [] }]);
        setActive(lines.length);
      }
      beep(true);
      setNote(item.tracking === "serial" ? `${item.name}: now scan each unit's IMEI / serial` : `${item.name} +1`);
      return;
    }
    const cur = lines[active];
    const curItem = cur ? byId.get(cur.itemId) : null;
    if (cur && curItem?.tracking === "serial") {
      const sn = code.replace(/[\s-]+/g, "").toUpperCase();
      if (cur.serials.includes(sn) || lines.some((l) => l.serials.includes(sn))) {
        beep(false);
        setNote(`${fmtSerial(sn)} is already scanned`);
        return;
      }
      setLines(lines.map((l, i) => (i === active ? { ...l, serials: [...l.serials, sn] } : l)));
      beep(true);
      setNote(`${curItem.name} · ${fmtSerial(sn)} ✓`);
      return;
    }
    beep(false);
    setNote(`${code} isn't a shop product's barcode. Pick the product below first, or add the barcode to it in Products`);
  };
  useScanner(onScan, !camera);

  const units = lines.reduce((n, l) => n + (byId.get(l.itemId)?.tracking === "serial" ? l.serials.length : Number(l.qty) || 0), 0);
  const cost = lines.reduce((n, l) => n + toMinor(l.cost) * (byId.get(l.itemId)?.tracking === "serial" ? l.serials.length : Number(l.qty) || 0), 0);
  const submit = async () => {
    setBusy(true);
    try {
      const r = await api<{ units: number; dupes: string[] }>(till("/admin/receive"), { body: { supplier, ref, lines: lines.map((l) => ({ itemId: l.itemId, qty: Number(l.qty) || 0, cost: l.cost ? toMinor(l.cost) : null, serials: l.serials })) } });
      toast(`${r.units} unit${r.units === 1 ? "" : "s"} added to shop stock${r.dupes.length ? ` · ${r.dupes.length} duplicate serial(s) skipped` : ""}`);
      setLines([]);
      setRef("");
      setActive(-1);
      void qc.invalidateQueries({ queryKey: ["admin-items"] });
      void qc.invalidateQueries({ queryKey: ["moves"] });
      void qc.invalidateQueries({ queryKey: ["catalog"] });
    } catch (e) {
      toast(e instanceof Error ? e.message : "Couldn't receive", "err");
    } finally {
      setBusy(false);
    }
  };
  const [pick, setPick] = useState("");
  return (
    <Card title="Receive a delivery" right={<span className="text-[13px] text-slate-500">{units} units · cost {money(cost)}</span>}>
      <div className="flex flex-col gap-3 px-5 pb-5">
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Supplier">
            <Input value={supplier} onChange={(e) => setSupplier(e.target.value)} />
          </Field>
          <Field label="Invoice / reference">
            <Input value={ref} onChange={(e) => setRef(e.target.value)} />
          </Field>
        </div>
        <div className="flex gap-2">
          <form onSubmit={(e) => { e.preventDefault(); if (scan) onScan(scan); }} className="flex h-12 min-w-0 flex-1 items-center gap-2 border-2 border-brand px-3">
            <input data-scan-target value={scan} onChange={(e) => setScan(e.target.value)} placeholder="Scan the box barcode, then each unit's IMEI / serial" className="min-w-0 flex-1 font-mono text-[15px] font-bold outline-none" />
          </form>
          <Button onClick={() => setCamera(true)} className="!h-12">
            Camera
          </Button>
        </div>
        {note ? <p className="bg-slate-100 px-3 py-2 text-[13px] font-semibold">{note}</p> : null}
        <div className="flex gap-2">
          <Select value={pick} onChange={(e) => setPick(e.target.value)} className="min-w-0 flex-1">
            <option value="">Or pick a product…</option>
            {items.filter((i) => i.active && i.tracking !== "service").map((i) => (
              <option key={i.id} value={i.id}>
                {i.name}
              </option>
            ))}
          </Select>
          <Button disabled={!pick} onClick={() => { if (!lines.some((l) => l.itemId === pick)) { setLines([...lines, { itemId: pick, qty: "", cost: "", serials: [] }]); setActive(lines.length); } setPick(""); }}>
            Add line
          </Button>
        </div>
        {lines.length ? (
          <div className="overflow-x-auto border border-slate-200">
            <table className="w-full border-collapse">
              <thead>
                <tr>
                  {["Item", "Units", "Unit cost", ""].map((h) => (
                    <th key={h} className={th}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {lines.map((l, i) => {
                  const it = byId.get(l.itemId);
                  if (!it) return null;
                  return (
                    <tr key={l.itemId} onClick={() => setActive(i)} className={cx("align-top", active === i && "bg-brand-tint")}>
                      <td className={td}>
                        <b>{it.name}</b>
                        {it.tracking === "serial" ? (
                          <div className="mt-1.5 flex max-w-[460px] flex-wrap gap-1">
                            {l.serials.map((sn) => (
                              <button key={sn} type="button" title="Remove" onClick={(e) => { e.stopPropagation(); setLines(lines.map((x, j) => (j === i ? { ...x, serials: x.serials.filter((s) => s !== sn) } : x))); }} className="bg-white px-1.5 py-0.5 font-mono text-[11px] ring-1 ring-slate-200">
                                {fmtSerial(sn)} ×
                              </button>
                            ))}
                            {!l.serials.length ? <span className="text-xs text-amber-800">{active === i ? "Scan each unit now" : "Tap to select, then scan units"}</span> : null}
                          </div>
                        ) : null}
                      </td>
                      <td className={td}>{it.tracking === "serial" ? <span className="font-mono font-bold">{l.serials.length}</span> : <Input inputMode="numeric" value={l.qty} onChange={(e) => setLines(lines.map((x, j) => (j === i ? { ...x, qty: e.target.value.replace(/\D/g, "") } : x)))} className="!h-9 w-20 font-mono" />}</td>
                      <td className={td}>
                        <Input inputMode="decimal" value={l.cost} onChange={(e) => setLines(lines.map((x, j) => (j === i ? { ...x, cost: e.target.value } : x)))} placeholder="0.00" className="!h-9 w-28 font-mono" />
                      </td>
                      <td className={td}>
                        <button type="button" onClick={(e) => { e.stopPropagation(); setLines(lines.filter((_, j) => j !== i)); setActive(-1); }} className="text-xs font-semibold text-red-700">
                          Remove
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : null}
        <div className="flex justify-end">
          <Button tone="primary" onClick={submit} disabled={busy || !units}>
            {busy ? "Saving…" : `Add ${units} to shop stock`}
          </Button>
        </div>
      </div>
      {camera ? <CameraScanner title="Scan barcodes and IMEIs" onCode={onScan} onClose={() => setCamera(false)}><p className="p-4 text-sm">{note}</p></CameraScanner> : null}
    </Card>
  );
}

function Transfer({ items, preselect }: { items: Item[]; preselect: string | null }) {
  const toast = useToast();
  const qc = useQueryClient();
  const linked = items.filter((i) => i.webSlug && i.tracking !== "service");
  const [itemId, setItemId] = useState(preselect ?? "");
  const [dir, setDir] = useState<"to_shop" | "to_web">("to_shop");
  const [qty, setQty] = useState("");
  const [serials, setSerials] = useState<string[]>([]);
  const [scan, setScan] = useState("");
  const it = linked.find((i) => i.id === itemId);
  const n = it?.tracking === "serial" ? serials.length : Number(qty) || 0;
  useScanner((c) => {
    const sn = c.replace(/[\s-]+/g, "").toUpperCase();
    if (it?.tracking === "serial" && !serials.includes(sn)) setSerials([...serials, sn]);
  });
  const submit = async () => {
    try {
      const r = await api<{ qty: number; ref: string }>(till("/admin/transfer"), { body: { itemId, direction: dir, qty: Number(qty) || 0, serials } });
      toast(`Moved ${r.qty} ${dir === "to_shop" ? "from the website to the shop" : "from the shop to the website"} · ${r.ref}`);
      setQty("");
      setSerials([]);
      void qc.invalidateQueries({ queryKey: ["admin-items"] });
      void qc.invalidateQueries({ queryKey: ["moves"] });
    } catch (e) {
      toast(e instanceof ApiError ? e.message : "Transfer failed", "err");
    }
  };
  return (
    <Card title="Transfer between shop & website">
      <div className="grid gap-4 px-5 pb-5 lg:grid-cols-2">
        <div className="flex flex-col gap-3">
          <div className="grid grid-cols-2 border border-slate-200">
            <button type="button" onClick={() => setDir("to_shop")} className={cx("h-11 text-sm font-semibold", dir === "to_shop" ? "bg-slate-900 text-white" : "bg-white")}>
              Website → Shop
            </button>
            <button type="button" onClick={() => setDir("to_web")} className={cx("h-11 text-sm font-semibold", dir === "to_web" ? "bg-slate-900 text-white" : "bg-white")}>
              Shop → Website
            </button>
          </div>
          <Field label="Product (linked to the website)">
            <Select value={itemId} onChange={(e) => { setItemId(e.target.value); setSerials([]); }}>
              <option value="">Choose…</option>
              {linked.map((i) => (
                <option key={i.id} value={i.id}>
                  {i.name}
                </option>
              ))}
            </Select>
          </Field>
          {it?.tracking === "serial" ? (
            <Field label="Scan the units being moved" hint={dir === "to_shop" ? "Each unit's IMEI / serial is added to shop stock" : "Units must be in shop stock"}>
              <form onSubmit={(e) => { e.preventDefault(); const sn = scan.replace(/[\s-]+/g, "").toUpperCase(); if (sn && !serials.includes(sn)) setSerials([...serials, sn]); setScan(""); }}>
                <Input data-scan-target value={scan} onChange={(e) => setScan(e.target.value)} placeholder="IMEI / serial" className="font-mono" />
              </form>
              <div className="flex flex-wrap gap-1">
                {serials.map((s) => (
                  <button key={s} type="button" onClick={() => setSerials(serials.filter((x) => x !== s))} className="bg-slate-100 px-1.5 py-0.5 font-mono text-[11px]">
                    {fmtSerial(s)} ×
                  </button>
                ))}
              </div>
            </Field>
          ) : it ? (
            <Field label="Units">
              <Input inputMode="numeric" value={qty} onChange={(e) => setQty(e.target.value.replace(/\D/g, ""))} className="font-mono text-lg" />
            </Field>
          ) : null}
        </div>
        {it ? (
          <div className="flex flex-col gap-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-slate-50 p-3">
                <span className="text-xs text-slate-500">Website</span>
                <div className="font-mono text-xl font-bold">{it.webStock == null ? "∞" : `${it.webStock} → ${dir === "to_shop" ? it.webStock - n : it.webStock + n}`}</div>
              </div>
              <div className="bg-brand-tint p-3">
                <span className="text-xs text-brand-ink">Shop</span>
                <div className="font-mono text-xl font-bold">
                  {it.onHand ?? 0} → {dir === "to_shop" ? (it.onHand ?? 0) + n : (it.onHand ?? 0) - n}
                </div>
              </div>
            </div>
            <Button tone="primary" onClick={submit} disabled={!n}>
              Transfer {n} unit{n === 1 ? "" : "s"}
            </Button>
          </div>
        ) : (
          <p className="text-sm text-slate-500">{linked.length ? "Choose a product." : "No products are linked to the website yet. Import them in Products."}</p>
        )}
      </div>
    </Card>
  );
}

function FindUnit() {
  const [sn, setSn] = useState("");
  const [go, setGo] = useState("");
  const q = useQuery({ queryKey: ["unit", go], queryFn: () => api<{ unit: { serial: string; status: string; receivedRef: string | null }; item: { name: string; price: number } | null; trail: Move[] }>(till(`/admin/units/${encodeURIComponent(go)}`)), enabled: !!go, retry: false });
  return (
    <Card title="Find a unit">
      <div className="flex flex-col gap-3 px-5 pb-5 lg:flex-row lg:items-start">
        <form onSubmit={(e) => { e.preventDefault(); setGo(sn.replace(/[\s-]+/g, "").toUpperCase()); }} className="flex w-full max-w-sm flex-col gap-1.5">
          <Input value={sn} onChange={(e) => setSn(e.target.value)} placeholder="Scan or type an IMEI / serial" className="font-mono" />
          <span className="text-xs text-slate-500">See where that exact device has been.</span>
        </form>
        {q.data ? (
          <div className="flex min-w-0 flex-1 flex-col gap-2">
            <b className="text-sm">
              {q.data.item?.name} · {fmtSerial(q.data.unit.serial)} · <Badge tone={q.data.unit.status === "in_stock" ? "brand" : "slate"}>{q.data.unit.status.replace("_", " ")}</Badge>
            </b>
            <div className="flex flex-wrap">
              {q.data.trail.map((m) => (
                <div key={m.id} className="min-w-[150px] flex-1 border-t-4 border-brand pr-3 pt-2">
                  <b className="text-[13px]">{KIND_LABEL[m.kind] ?? m.kind}</b>
                  <span className="block text-xs text-slate-500">
                    {dateTime(m.at)} · {m.ref ?? ""} {m.staffName ? `· ${m.staffName}` : ""}
                  </span>
                </div>
              ))}
            </div>
          </div>
        ) : q.error ? (
          <p className="text-sm text-red-700">{(q.error as Error).message}</p>
        ) : null}
      </div>
    </Card>
  );
}

function Log() {
  const [kind, setKind] = useState("");
  const q = useQuery({ queryKey: ["moves", kind], queryFn: () => api<{ moves: Move[] }>(till(`/admin/moves?kind=${kind}`)) });
  return (
    <Card
      title="Stock log"
      right={
        <div className="flex flex-wrap gap-1.5">
          {[["", "All"], ["sales", "Sales"], ["returns", "Returns"], ["received", "Received"], ["transfers", "Transfers"], ["adjust", "Adjust & stocktake"]].map(([k, l]) => (
            <button key={k} type="button" onClick={() => setKind(k!)} className={cx("border px-2.5 py-1 text-xs", kind === k ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 bg-white")}>
              {l}
            </button>
          ))}
        </div>
      }
    >
      <div className="overflow-x-auto">
        <table className="w-full border-collapse">
          <thead>
            <tr>
              {["When", "What", "Product", "Change", "Unit", "Shop stock", "By", "Ref"].map((h) => (
                <th key={h} className={th}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {(q.data?.moves ?? []).map((m) => (
              <tr key={m.id}>
                <td className={cx(td, "text-slate-500")}>{dateTime(m.at)}</td>
                <td className={td}>
                  <Badge tone={KIND_TONE[m.kind] ?? "slate"}>{KIND_LABEL[m.kind] ?? m.kind}</Badge>
                </td>
                <td className={td}>{m.itemName}</td>
                <td className={cx(td, "font-mono font-bold")}>{m.qty > 0 ? `+${m.qty}` : m.qty}</td>
                <td className={cx(td, "font-mono")}>{m.serial ? fmtSerial(m.serial) : "-"}</td>
                <td className={cx(td, "font-mono")}>{m.balance}</td>
                <td className={td}>{m.staffName ?? "-"}</td>
                <td className={cx(td, "font-mono")}>{m.ref ?? ""}{m.note ? <span className="ml-2 font-sans text-xs text-slate-500">{m.note}</span> : null}</td>
              </tr>
            ))}
            {q.data && !q.data.moves.length ? (
              <tr>
                <td colSpan={8} className={cx(td, "py-6 text-center text-slate-500")}>
                  Nothing yet.
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>
    </Card>
  );
}
